import { useEffect, useState } from 'react';

import {
  GridEventListener,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import TableModels from '../types/tableModels.type';
import usePrepareRequestBody from './usePrepareRequestBody';

const useDataGridTable = () => {
  const apiRef = useGridApiRef();

  const [model, setModel] = useState<TableModels>({
    paginationModel: {
      pageSize: 25,
      page: 0,
    },
    filterModel: {
      items: [],
      quickFilterValues: [],
    },
    sortModel: [],
    rowSelectionModel: [],
  });

  const { requestBody } = usePrepareRequestBody({ model, apiRef });

  const setPaginationModel = (paginationModel: GridPaginationModel) => {
    setModel((prevModel) => ({
      ...prevModel,
      paginationModel: {
        ...prevModel.paginationModel,
        ...paginationModel,
      },
    }));
  };

  const setFilterModel = (filterModel: GridFilterModel) => {
    setModel((prevModel) => ({
      ...prevModel,
      filterModel: {
        ...prevModel.filterModel,
        ...filterModel,
      },
    }));
  };

  const setSortModel = (sortModel: GridSortModel) => {
    setModel((prevModel) => ({
      ...prevModel,
      sortModel: [...sortModel],
    }));
  };

  const setRowSelectionModel = (rowSelectionModel: GridRowSelectionModel) => {
    setModel((prevModel) => ({
      ...prevModel,
      rowSelectionModel: [...rowSelectionModel],
    }));
  };

  useEffect(() => {
    const handleFilterModelChange: GridEventListener<'filterModelChange'> = (
      filterModel,
    ) => {
      setFilterModel(filterModel);
    };

    const handleSortModelChange: GridEventListener<'sortModelChange'> = (
      sortModel,
    ) => {
      setSortModel(sortModel);
    };

    const handlePaginationModelChange: GridEventListener<
      'paginationModelChange'
    > = (paginationModel) => {
      setPaginationModel(paginationModel);
    };

    const handleRowSelectionChange: GridEventListener<'rowSelectionChange'> = (
      selectionModel,
    ) => {
      setRowSelectionModel(selectionModel);
    };

    const filterModelSubscription = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterModelChange,
    );

    const sortModelSubscription = apiRef.current.subscribeEvent(
      'sortModelChange',
      handleSortModelChange,
    );

    const paginationModelSubscription = apiRef.current.subscribeEvent(
      'paginationModelChange',
      handlePaginationModelChange,
    );

    const rowSelectionSubscription = apiRef.current.subscribeEvent(
      'rowSelectionChange',
      handleRowSelectionChange,
    );

    return () => {
      filterModelSubscription();
      sortModelSubscription();
      paginationModelSubscription();
      rowSelectionSubscription();
    };
  }, [apiRef]);

  return { apiRef, model, requestBody };
};

export default useDataGridTable;
